import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DragulaService } from 'ng2-dragula';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import {
  DashpivotEvent,
  EventNotifierService,
  EventTypes,
  Field,
  FieldKinds,
  List,
  LogicActions,
  LogicOperators,
  PhotoThumbnailSizes,
  Template,
  TemplateOrientations,
  YesNoValues,
} from '@site-mate/dashpivot-shared-library';

import { TemplatesService } from 'app/apps/templates.service';
import { FormCategoryService } from 'app/form-fields/category/edit/form-category.service';
import { SegmentService } from 'app/segment/segment.service';
import { FormCategoryOption } from 'app/shared/model/form-category.option.model';
import { FieldWeb, LogicRuleWeb } from 'app/shared/model/item.model';
import { ConfirmService } from 'app/shared/service/confirm.service';

@Component({
  selector: 'cc-logic-rule',
  templateUrl: './logic-rule.component.html',
  styleUrls: ['./logic-rule.component.scss'],
})
export class LogicRuleComponent implements OnInit, OnDestroy {
  private categorySub: Subscription;
  private unSubscribe = new Subject<void>();

  @Input() rule: LogicRuleWeb;
  @Input() field: FieldWeb;
  @Input() fields: FieldWeb[];
  @Input() template: Template;
  @Input() metadataList: Observable<List[]>;
  @Input() companyId: string;
  @Input() parentId: string;
  @Input() templateOrientation: TemplateOrientations;
  @Input() fieldUpdated: EventEmitter<any>;
  @Input() hasReachedPlanLimit: boolean;

  @Output() updated = new EventEmitter<FieldWeb>();
  @Output() remove = new EventEmitter<number>();
  @Output() clone = new EventEmitter<number>();
  @Output() validate = new EventEmitter<FieldWeb>();
  @Output() removeField = new EventEmitter<FieldWeb[]>();

  readonly dragulaId = 'template-fields-drag';

  logicOperators: string[] = [];
  logicActions = Object.values(LogicActions).filter((action) => action === LogicActions.ShowField);
  logicValues: FormCategoryOption[];
  isCategory: boolean;
  showLearnMore: boolean;

  constructor(
    private readonly confirmService: ConfirmService,
    private readonly templatesService: TemplatesService,
    private readonly segmentService: SegmentService,
    private readonly formCategoryService: FormCategoryService,
    private readonly dragulaService: DragulaService,
  ) {}

  get showFields() {
    return this.rule.logicAction === LogicActions.ShowField && this.rule.isOpen;
  }

  get emptyFields() {
    return !this.fields.some((field) => field.dependsOn?.logicRuleId === this.rule.id);
  }

  ngOnInit() {
    this.isCategory = this.field?.kind === FieldKinds.Category;
    this.showLearnMore = this.field.logicRules.indexOf(this.rule) === 0;

    this.loadValues(this.field, true);

    this.logicOperators = [LogicOperators.Equals];

    this.fieldUpdated.pipe(takeUntil(this.unSubscribe)).subscribe((field) => {
      this.loadValues(field, false);
    });

    this.dragulaService
      .drop(this.dragulaId)
      .pipe(
        takeUntil(this.unSubscribe),
        filter(({ target }) => target && target.getAttribute('data-field-id') === this.field._id),
      )
      .subscribe(() => this.onFieldUpdated());
  }

  trackByFieldIdentifier(_index: number, field: FieldWeb) {
    return `${field.id}-${field.kind}`;
  }

  ngOnDestroy() {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }

  toggleOpenFields() {
    this.rule.isOpen = !this.rule.isOpen;
  }

  filterSelectedValues(newValues: FormCategoryOption[]) {
    return this.rule.values ? this.rule.values.filter((value) => newValues.some((v) => v.id === value)) : [];
  }

  async onRemoveField(index: number) {
    try {
      await this.confirmService.confirmDelete('deleteField');

      this.triggerFieldEvent(index, EventTypes.TemplateFieldDeleted);

      const removedFields = this.templatesService.removeControl(index, this.fields);
      this.removeField.emit(removedFields);
      this.onFieldUpdated();
    } catch {
      // no-op
    }
  }

  onCloneField(index: number) {
    const clonedFields = this.templatesService.cloneField(this.fields[index], this.fields);
    this.fields.splice(index + 1, 0, ...clonedFields);
    this.onFieldUpdated();

    this.triggerFieldEvent(index, EventTypes.TemplateFieldCloned);
  }

  triggerFieldEvent(index: number, event: EventTypes) {
    const field = this.fields[index];
    const eventContext =
      field.kind === FieldKinds.Photo
        ? this.getPhotoEventContext(field.photoThumbnailSize || PhotoThumbnailSizes.Small)
        : field.kind;

    void EventNotifierService.notify(
      new DashpivotEvent(event, {
        Context: eventContext,
      }),
      this.segmentService,
    );
  }

  getPhotoEventContext(photoThumbnailSize: PhotoThumbnailSizes) {
    const eventContexts = {
      [PhotoThumbnailSizes.ExtraSmall]: 'Photos (XS)',
      [PhotoThumbnailSizes.Small]: 'Photos (S)',
      [PhotoThumbnailSizes.Medium]: 'Photos (M)',
      [PhotoThumbnailSizes.Large]: 'Photos (L)',
      [PhotoThumbnailSizes.ExtraLarge]: 'Photos (XL)',
    };
    return eventContexts[photoThumbnailSize];
  }

  private loadValues(field: Field, initialLoad: boolean) {
    if (this.isCategory) {
      this.loadCategoryValues(initialLoad);
    } else {
      this.loadYesNoValues(field);
    }
  }

  private loadYesNoValues(field: Field) {
    this.logicValues = Object.keys(YesNoValues)
      .filter((key) => field.hasNaButton || key !== 'NA')
      .map((key) => ({ id: YesNoValues[key], value: key, label: key }));
    this.rule.values = this.filterSelectedValues(this.logicValues);
  }

  private loadCategoryValues(initialLoad: boolean) {
    if (this.categorySub) {
      this.categorySub.unsubscribe();
    }

    this.categorySub = this.formCategoryService
      .getCategoryItems(this.field, this.metadataList, this.parentId)
      .pipe(
        takeUntil(this.unSubscribe),
        filter((values: any) => !initialLoad || values?.length),
      )
      .subscribe((values) => {
        this.logicValues = values;
        this.rule.values = this.filterSelectedValues(values);
      });
  }

  onRemoveRule() {
    this.remove.emit();
    this.validateLogicRules();
  }

  onCloneRule() {
    this.clone.emit();
    this.validateLogicRules();
  }

  onFieldUpdated() {
    this.updated.emit(this.field);
    this.validateLogicRules();
  }

  validateLogicRules() {
    this.validate.emit();
  }
}
